import React from "react";

const PasswordStrengthIndicator = ({ password }) => {
  const requirements = [
    {
      id: "length",
      label: "At least 8 characters",
      test: (pwd) => pwd.length >= 8,
    },
    {
      id: "uppercase",
      label: "At least one uppercase letter (A-Z)",
      test: (pwd) => /[A-Z]/.test(pwd),
    },
    {
      id: "lowercase", 
      label: "At least one lowercase letter (a-z)",
      test: (pwd) => /[a-z]/.test(pwd),
    },
    {
      id: "number",
      label: "At least one number (0-9)",
      test: (pwd) => /[0-9]/.test(pwd),
    },
    {
      id: "special",
      label: "At least one special character (!@#$%^&*)",
      test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
    },
  ];

  const passedRequirements = requirements.filter((req) => req.test(password));
  const strengthPercentage = (passedRequirements.length / requirements.length) * 100;

  const getStrengthColor = () => {
    if (strengthPercentage < 40) return "#dc3545"; // Red
    if (strengthPercentage < 80) return "#ffc107"; // Yellow  
    return "#28a745"; // Green
  };

  const getStrengthLabel = () => {
    if (strengthPercentage < 40) return "Weak";
    if (strengthPercentage < 80) return "Medium";
    return "Strong";
  };

  return (
    <div className="password-strength-indicator mt-3">
      {/* Strength Bar */}
      <div className="mb-2">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <small className="text-muted">Password Strength:</small>
          <small 
            className="font-weight-bold"
            style={{ color: getStrengthColor() }}
          >
            {getStrengthLabel()}
          </small>
        </div>
        <div 
          className="progress" 
          style={{ height: "6px", backgroundColor: "#e9ecef" }}
        >
          <div
            className="progress-bar"
            style={{
              width: `${strengthPercentage}%`,
              backgroundColor: getStrengthColor(),
              transition: "all 0.3s ease",
            }}
          />
        </div>
      </div>

      {/* Requirements Checklist */}
      <div className="password-requirements">
        <small className="text-muted d-block mb-2">Password must contain:</small>
        {requirements.map((requirement) => {
          const isValid = requirement.test(password);
          return (
            <div
              key={requirement.id}
              className="d-flex align-items-center mb-1"
              style={{ fontSize: "12px" }}
            >
              <i
                className={`fas ${
                  isValid ? "fa-check-circle text-success" : "fa-times-circle text-muted"
                } mr-2`}
                style={{ width: "14px" }}
              />
              <span
                className={isValid ? "text-success" : "text-muted"}
                style={{
                  textDecoration: isValid ? "line-through" : "none",
                  opacity: isValid ? 0.7 : 1,
                }}
              >
                {requirement.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Helper function to validate password strength
export const validatePasswordStrength = (password) => {
  const requirements = [
    { test: (pwd) => pwd.length >= 8, message: "Password must be at least 8 characters" },
    { test: (pwd) => /[A-Z]/.test(pwd), message: "Password must contain at least one uppercase letter" },
    { test: (pwd) => /[a-z]/.test(pwd), message: "Password must contain at least one lowercase letter" },
    { test: (pwd) => /[0-9]/.test(pwd), message: "Password must contain at least one number" },
    { test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd), message: "Password must contain at least one special character" },
  ];

  const failedRequirements = requirements.filter((req) => !req.test(password));
  
  return {
    isValid: failedRequirements.length === 0,
    errors: failedRequirements.map((req) => req.message),
    strength: ((requirements.length - failedRequirements.length) / requirements.length) * 100,
  };
};

export default PasswordStrengthIndicator;
